import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { BrickWall, Plus, Trash2 } from "lucide-react";
import client, { absoluteUrl } from "../api/client";
import "../Moodboard/Moodboard.css";
import "./Architect.css";

export default function WallList() {
  const navigate = useNavigate();
  const [walls, setWalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    client
      .get("/api/visualisations")
      .then(({ data }) => setWalls(data.items || []))
      .catch(() => setError("Could not load your walls."))
      .finally(() => setLoading(false));
  }, []);

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await client.post("/api/visualisations", { title: "Untitled wall" });
      navigate(`/visualiser/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not start a new wall.");
      setCreating(false);
    }
  };

  const remove = async (wall) => {
    if (!window.confirm(`Delete "${wall.title}"?`)) return;
    try {
      await client.delete(`/api/visualisations/${wall.id}`);
      setWalls((current) => current.filter((entry) => entry.id !== wall.id));
    } catch {
      toast.error("Could not delete the wall.");
    }
  };

  return (
    <main className="mb-page ar-page">
      <ToastContainer position="bottom-right" />
      <header className="mb-page-head">
        <div>
          <p className="mb-kicker">Studio</p>
          <h1>Wall Visualiser</h1>
        </div>
        <button type="button" className="mb-primary" onClick={create} disabled={creating}>
          <Plus size={16} strokeWidth={2} aria-hidden="true" />
          New wall
        </button>
      </header>

      {error && <p className="mb-error">{error}</p>}
      {loading ? (
        <p className="mb-muted">Loading walls...</p>
      ) : !walls.length && !error ? (
        <section className="mb-empty">
          <h2>See it on the client's wall</h2>
          <p>
            Upload a photo of the room, mark the wall's four corners and enter its size. Artwork, wallpaper and murals then
            appear at their real size and in perspective, ready to export or add to a project.
          </p>
          <button type="button" className="mb-primary" onClick={create} disabled={creating}>
            <Plus size={16} strokeWidth={2} aria-hidden="true" />
            New wall
          </button>
        </section>
      ) : (
        <section className="ar-project-grid">
          {walls.map((wall) => (
            <article key={wall.id} className="ar-project-card">
              <Link to={`/visualiser/${wall.id}`} className="ar-project-cover" aria-label={`Open ${wall.title}`}>
                {wall.photo ? <img src={absoluteUrl(wall.photo.url) || undefined} alt="" loading="lazy" /> : <BrickWall size={30} strokeWidth={1.4} aria-hidden="true" />}
              </Link>
              <div className="ar-project-meta">
                <div>
                  <h2>
                    <Link to={`/visualiser/${wall.id}`}>{wall.title}</Link>
                  </h2>
                  <p>{wall.project_name ? <Link to={`/projects/${wall.project_id}`}>{wall.project_name}</Link> : "Not in a project"}</p>
                </div>
                <button type="button" className="mb-icon-button danger" onClick={() => remove(wall)} title="Delete wall" aria-label={`Delete ${wall.title}`}>
                  <Trash2 size={15} strokeWidth={1.8} />
                </button>
              </div>
            </article>
          ))}
        </section>
      )}
    </main>
  );
}
